import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CalendarDays, ListChecks, Sparkles, UtensilsCrossed } from "lucide-react";

const FEATURES = [
  { icon: CalendarDays, title: "Family Calendar", desc: "Sync Google & iCal feeds for everyone." },
  { icon: ListChecks, title: "Chores & Rewards", desc: "Assign chores, track streaks, redeem rewards." },
  { icon: UtensilsCrossed, title: "Meal Planning", desc: "Plan the week, auto-build the shopping list." },
  { icon: Sparkles, title: "Cleaning", desc: "Room routines that actually get done." },
];

function AppleBadge() {
  return (
    <a
      href="#"
      data-testid="download-apple-btn"
      className="flex items-center gap-3 rounded-2xl bg-ink px-5 py-2.5 text-white transition-transform hover:scale-[1.02]"
    >
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 8.9 7.31c1.22.06 2.07.66 2.78.71 1.06-.22 2.08-.84 3.22-.75 1.36.11 2.39.66 3.06 1.66-2.82 1.72-2.37 5.43.32 6.6-.53 1.4-1.22 2.79-2.19 3.86l-.04-.02zM12.03 7.25c-.13-2.02 1.51-3.7 3.4-3.85.26 2.16-1.94 3.94-3.4 3.85z" />
      </svg>
      <span className="text-left leading-tight">
        <span className="block text-[10px] font-medium uppercase tracking-wide text-white/80">Download on the</span>
        <span className="block font-display text-lg font-semibold">App Store</span>
      </span>
    </a>
  );
}

function GoogleBadge() {
  return (
    <a
      href="#"
      data-testid="download-google-btn"
      className="flex items-center gap-3 rounded-2xl bg-ink px-5 py-2.5 text-white transition-transform hover:scale-[1.02]"
    >
      <svg className="h-7 w-7" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#EA4335" d="M3.6 2.3 13 11.7 15.6 9 5.2 3C4.7 2.7 4.1 2.4 3.6 2.3z" />
        <path fill="#4285F4" d="M3.6 2.3C3.2 2.5 3 2.9 3 3.5v17c0 .6.2 1 .6 1.2L13 12.3 3.6 2.3z" />
        <path fill="#FBBC05" d="m17.9 9.9-2.3-1.3-2.9 3 2.9 2.9 2.3-1.3c.9-.5.9-2.9 0-3.3z" />
        <path fill="#34A853" d="M3.6 21.7c.5-.1 1.1-.4 1.6-.7l10.4-6-2.6-2.7-9.4 9.4z" />
      </svg>
      <span className="text-left leading-tight">
        <span className="block text-[10px] font-medium uppercase tracking-wide text-white/80">Get it on</span>
        <span className="block font-display text-lg font-semibold">Google Play</span>
      </span>
    </a>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2.5">
          <img src="/logo.png" alt="BentoLife" className="h-10 w-10 rounded-xl border border-border" />
          <span className="font-display text-2xl font-semibold text-ink">BentoLife</span>
        </div>
        <Link to="/login">
          <Button variant="outline" className="rounded-full" data-testid="landing-login-btn">
            Sign in
          </Button>
        </Link>
      </header>

      <section className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-12 lg:grid-cols-2 lg:py-20">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center rounded-3xl border border-border bg-muted">
              <img src="/logo.png" alt="BentoLife logo" className="h-14 w-14" />
            </div>
            <span className="font-display text-3xl font-bold text-ink">BentoLife</span>
          </div>

          <h1 className="font-display text-4xl font-bold leading-tight text-ink sm:text-5xl">
            Everything your family needs, <br className="hidden sm:block" />
            <span className="text-muted-foreground">calm and in one place.</span>
          </h1>
          <p className="max-w-md text-lg text-muted-foreground">
            Chores, calendar, meal planning, cleaning and rewards — one quiet, shared home base
            everyone in the family can rely on.
          </p>

          <div className="flex flex-col gap-3 pt-1">
            <div className="flex flex-wrap gap-3">
              <AppleBadge />
              <GoogleBadge />
            </div>
            <p className="text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-ink underline underline-offset-2" data-testid="landing-signin-link">
                Sign in on the web
              </Link>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <div key={f.title} className="bento-tile flex flex-col items-start gap-3 p-5">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary text-ink">
                <f.icon className="h-6 w-6" />
              </div>
              <span className="font-display text-base font-semibold text-ink">{f.title}</span>
              <span className="text-sm text-muted-foreground">{f.desc}</span>
            </div>
          ))}
        </div>
      </section>

      <footer className="mx-auto max-w-6xl px-6 py-10 text-sm text-muted-foreground">
        © {new Date().getFullYear()} BentoLife · Made for families
      </footer>
    </div>
  );
}
